import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useEventListener } from 'usehooks-ts';
import { gridClasses, useGridApiRef } from '@mui/x-data-grid-pro';

import {
  ACTION_CELL_KEYS,
  ADD_ROW_ID,
  PENDING_ROW_ID,
  ACTION_FIELD_LABELS,
  ACTION_STATUSES,
  ACTION_STATUS_LABELS,
  ACTION_STATUS_COLORS,
  ORDERED_ACTIONS_STATUSES,
  ACTION_TRACKER_CELL_KEYS,
} from 'constants/actionTracker';
import { ALL_OPTIONS_VALUE, SORT_OPTIONS } from 'constants/common';
import { DATA_GRID_COLUMN_TYPE } from 'constants/components';
import { API_DATE_FORMAT, formatDate } from 'helper/DateLib';
import { formatUserName } from 'helper/formatUserName';
import { useCurrentUserInfo } from 'hooks/useCurrentUserInfo';
import { DataGridTable } from 'components/complex/DataGridTable';
import {
  DataGridAutocomplete,
  DataGridDateChip,
  DataGridDatePicker,
  DataGridMultilineEditCell,
  DataGridOwnerCell,
  DataGridStatusChip,
  DataGridStatusSelect,
} from 'components/simple';
import { CrossIcon as CloseIcon, DeleteIcon, ManageUsersIcon } from 'assets/icons';

const styles = {
  dataGrid: ({ palette, shape }) => {
    const border = `1px solid ${palette.colors.lightGrey}`;

    return {
      border: 'none',
      width: '100%',
      [`& .${gridClasses.columnHeaders}`]: {
        border,
        marginTop: 0,
        overflow: 'hidden',
        borderRadius: `${2.5 * shape.borderRadius}px ${2.5 * shape.borderRadius}px 0 0`,
        backgroundColor: palette.colors.veryLightGrey,
      },
      [`& .${gridClasses.row}`]: {
        border,
        backgroundColor: 'transparent !important',
        borderTop: 'none',
        borderRadius: 0,
        cursor: 'pointer',
        minHeight: '84px !important',
        [`&.filled-row`]: {
          backgroundColor: `${palette.colors.veryLightGrey} !important`,
        },
      },
      [`& .${gridClasses.cell}`]: {
        whiteSpace: 'pre-wrap !important',
        wordBreak: 'break-word',
        alignItems: 'flex-start',
        paddingBlock: 2,
        '&.add-row-cell': {
          alignItems: 'center',
        },
      },
    };
  },
  emptyTable: ({ palette }) => ({
    backgroundColor: palette.colors.veryLightGrey,
    borderColor: palette.colors.lightGrey,
  }),
  cancelAction: ({ palette }) => ({
    width: 24,
    height: 24,
    borderRadius: '50%',
    border: `2px solid ${palette.primary.main}`,
    color: `${palette.primary.main} !important`,
    backgroundColor: palette.background.paper,
    svg: {
      width: '12px !important',
      height: '12px !important',
    },
  }),
};

const checkTextEmpty = (value) => !value?.trim();

const getRowDataToUpdate = (changedField, newRowData) => {
  const changedFieldData = newRowData[changedField];

  switch (changedField) {
    case ACTION_CELL_KEYS.ACTION_TEXT:
    case ACTION_CELL_KEYS.DESIRED_OUTCOME:
      return {
        [changedField]: changedFieldData?.trim() ?? '',
      };
    case ACTION_CELL_KEYS.DEADLINE:
      return {
        [changedField]: formatDate(changedFieldData, API_DATE_FORMAT),
      };
    case ACTION_CELL_KEYS.OWNER_DETAILS:
      return {
        owner: changedFieldData?.id || null,
      };
    default:
      return {
        [changedField]: changedFieldData,
      };
  }
};

export const JournalActionsTable = ({
  entry,
  fetchActionOwners = undefined,
  onUpdateAction,
  onDeleteAction = undefined,
  onGoToManageUsers = undefined,
  pendingRow = null,
  onCreatePendingRow,
  onUpdatePendingRow,
  onCancelPendingRow,
  onSubmitPendingRow,
  withEditPermissions = false,
  isReadOnlyJournal,
  detailColumnConfig = null,
  getRowMenuItems = null,
  defaultSortModel = null,
}) => {
  const apiRef = useGridApiRef();
  const { userId } = useCurrentUserInfo();
  const [sortModel, setSortModel] = useState(defaultSortModel);
  const [filterModelItems, setFilterModelItems] = useState([]);
  const isSubmittingRef = useRef(false);
  const isCancelingRef = useRef(false);
  const cellValidationErrorRef = useRef(null);

  // The fifth column is configurable: Owner (autocomplete, mandatory) for Project Journals, or a
  // free-text field (e.g. Support Needed, optional) for Personal Journals.
  const detailField = detailColumnConfig?.field ?? ACTION_CELL_KEYS.OWNER_DETAILS;
  const isDetailFieldRequired = detailColumnConfig ? detailColumnConfig.isRequired : true;
  const printableFields = useMemo(
    () => [ACTION_CELL_KEYS.ACTION_TEXT, ACTION_CELL_KEYS.DESIRED_OUTCOME, detailField],
    [detailField],
  );

  const handleCellClick = useCallback(
    (params) => {
      if (!params.isEditable || params.cellMode === 'edit') {
        return;
      }

      apiRef.current.startCellEditMode({ id: params.id, field: params.field });
    },
    [apiRef],
  );

  const handleCellClickCapture = useCallback((e) => {
    const error = cellValidationErrorRef.current;

    if (!error) {
      return;
    }

    const cell = e.target.closest(`.${gridClasses.cell}`);

    if (!cell) {
      return;
    }

    const row = cell.closest(`.${gridClasses.row}`);

    if (!row) {
      return;
    }

    const rowId = row.getAttribute('data-id');
    const field = cell.getAttribute('data-field');

    if (String(error.rowId) !== rowId || error.field !== field) {
      e.stopPropagation();
    }
  }, []);

  const hasPendingRowForEntry = pendingRow?.entryId === entry.id;

  // Focus the first cell of the pending row after it's created
  useEffect(() => {
    if (hasPendingRowForEntry) {
      // Use setTimeout to ensure the row is rendered before focusing
      setTimeout(() => {
        try {
          apiRef.current.startCellEditMode({
            id: PENDING_ROW_ID,
            field: ACTION_CELL_KEYS.ACTION_TEXT,
          });
        } catch {
          // Row may not be rendered yet
        }
      }, 50);
    }
  }, [hasPendingRowForEntry, apiRef]);

  const stopPendingRowEditAndCancel = useCallback(() => {
    isCancelingRef.current = true;

    const editingState = apiRef.current.state.editRows[PENDING_ROW_ID];

    if (editingState) {
      const editingField = Object.keys(editingState)[0];

      if (editingField) {
        apiRef.current.stopCellEditMode({
          id: PENDING_ROW_ID,
          field: editingField,
          ignoreModifications: true,
        });
      }
    }

    onCancelPendingRow();
    isCancelingRef.current = false;
  }, [apiRef, onCancelPendingRow]);

  useEventListener('keydown', (event) => {
    if (!hasPendingRowForEntry || !pendingRow) {
      return;
    }

    if (event.key === 'Escape') {
      const hasDefaultValues =
        ((!pendingRow[ACTION_CELL_KEYS.ACTION_TEXT] &&
          !pendingRow[ACTION_CELL_KEYS.DESIRED_OUTCOME] &&
          !pendingRow[ACTION_CELL_KEYS.STATUS]) ||
          pendingRow[ACTION_CELL_KEYS.STATUS] === ACTION_STATUSES.NOT_STARTED) &&
        !pendingRow[detailField];

      const editingState = apiRef.current.state.editRows[PENDING_ROW_ID];
      const hasUncommittedValue = editingState && Object.values(editingState).some((cellState) => cellState.value);

      if (hasDefaultValues && !hasUncommittedValue) {
        stopPendingRowEditAndCancel();
      }
    }
  });

  const handleProcessRowUpdate = useCallback(
    async (newRow, oldRow) => {
      if (!newRow || !oldRow) {
        return oldRow ?? newRow;
      }

      if (newRow.id === ADD_ROW_ID) {
        return oldRow;
      }

      if (newRow.id === PENDING_ROW_ID) {
        if (isCancelingRef.current) {
          return oldRow;
        }

        const updatedPendingRow = { ...newRow };

        delete updatedPendingRow.id;
        onUpdatePendingRow(updatedPendingRow);

        const actionText = newRow[ACTION_CELL_KEYS.ACTION_TEXT]?.trim();
        const desiredOutcome = newRow[ACTION_CELL_KEYS.DESIRED_OUTCOME]?.trim();
        const deadline = newRow[ACTION_CELL_KEYS.DEADLINE];
        const isDetailFilled = !isDetailFieldRequired || newRow[detailField];

        if (actionText && desiredOutcome && deadline && isDetailFilled && !isSubmittingRef.current) {
          isSubmittingRef.current = true;

          const isSubmitted = await onSubmitPendingRow({
            entry: entry.id,
            ownerDetails: newRow[ACTION_CELL_KEYS.OWNER_DETAILS],
            data: [
              ACTION_CELL_KEYS.ACTION_TEXT,
              ACTION_CELL_KEYS.DESIRED_OUTCOME,
              ACTION_CELL_KEYS.STATUS,
              ACTION_CELL_KEYS.DEADLINE,
              detailField,
            ].reduce(
              (acc, field) => ({
                ...acc,
                ...getRowDataToUpdate(field, newRow),
              }),
              {},
            ),
          }).then(
            () => true,
            () => false,
          );

          isSubmittingRef.current = false;

          if (!isSubmitted) {
            return newRow;
          }
        }

        return newRow;
      }

      if (JSON.stringify(newRow) === JSON.stringify(oldRow)) {
        return oldRow;
      }

      const changedField = Object.keys(newRow).find((key) => {
        const oldValue = oldRow[key];
        const newValue = newRow[key];

        return key === ACTION_CELL_KEYS.OWNER_DETAILS ? newValue?.id !== oldValue?.id : newValue !== oldValue;
      });

      if (changedField) {
        onUpdateAction({
          actionId: newRow.id,
          entryId: entry.id,
          data: getRowDataToUpdate(changedField, newRow),
          newRow,
        });
      }

      return newRow;
    },
    [onUpdatePendingRow, onSubmitPendingRow, entry.id, onUpdateAction, detailField, isDetailFieldRequired],
  );

  const handleAddRow = useCallback(() => {
    onCreatePendingRow(entry.id);
  }, [onCreatePendingRow, entry.id]);

  const handleCancelMouseDown = useCallback(() => {
    isCancelingRef.current = true;
  }, []);

  const defaultRowMenuItems = useCallback(
    (row) => [
      {
        label: 'Manage Users',
        icon: <ManageUsersIcon />,
        onClick: () => onGoToManageUsers(),
        showInMenu: true,
      },
      {
        label: 'Delete Action',
        icon: <DeleteIcon />,
        onClick: () => onDeleteAction(row),
        showInMenu: true,
      },
    ],
    [onGoToManageUsers, onDeleteAction],
  );

  const getActionItemActions = useCallback(
    (row) =>
      row.id === PENDING_ROW_ID
        ? [
            {
              label: 'Cancel',
              icon: <CloseIcon />,
              onMouseDown: handleCancelMouseDown,
              onClick: stopPendingRowEditAndCancel,
              sx: styles.cancelAction,
            },
          ]
        : (getRowMenuItems ?? defaultRowMenuItems)(row),
    [handleCancelMouseDown, stopPendingRowEditAndCancel, getRowMenuItems, defaultRowMenuItems],
  );

  const columns = useMemo(() => {
    const validateRequiredField = (field, checkError) => (params) => {
      const hasError = checkError(params.props.value);

      if (params.id === PENDING_ROW_ID) {
        if (hasError) {
          cellValidationErrorRef.current = { rowId: params.id, field };
        } else if (
          cellValidationErrorRef.current?.rowId === params.id &&
          cellValidationErrorRef.current?.field === field
        ) {
          cellValidationErrorRef.current = null;
        }
      }

      return { ...params.props, error: hasError };
    };

    const ownerColumn = {
      field: ACTION_CELL_KEYS.OWNER_DETAILS,
      headerName: ACTION_FIELD_LABELS[ACTION_CELL_KEYS.OWNER_DETAILS],
      align: 'left',
      headerAlign: 'left',
      width: 200,
      xxlWidth: 250,
      editable: true,
      sortable: false,
      valueFormatter: (value, row) => (row?.owner ? formatUserName(value) : ''),
      preProcessEditCellProps: validateRequiredField(ACTION_CELL_KEYS.OWNER_DETAILS, (value) => !value),
      renderCell: (params) => <DataGridOwnerCell value={params.value} />,
      renderEditCell: (params) => (
        <DataGridAutocomplete
          noOptionsDescription="Please invite the individual by going to the 'Manage Users' section of this project. They must accept the invitation and activate their account to then be added as an owner."
          params={params}
          fetchOptions={fetchActionOwners}
        />
      ),
    };

    return [
      {
        field: ACTION_CELL_KEYS.ACTION_TEXT,
        headerName: ACTION_FIELD_LABELS[ACTION_CELL_KEYS.ACTION_TEXT],
        flex: 1,
        editable: true,
        sortable: false,
        align: 'left',
        headerAlign: 'left',
        width: 250,
        xxlWidth: 315,
        colSpan: (value, row) => (row.id === ADD_ROW_ID ? 6 : 1),
        preProcessEditCellProps: validateRequiredField(ACTION_CELL_KEYS.ACTION_TEXT, checkTextEmpty),
        renderCell: (params) => <DataGridMultilineEditCell value={params.value} />,
        renderEditCell: (params) => <DataGridMultilineEditCell params={params} />,
      },
      {
        field: ACTION_CELL_KEYS.DESIRED_OUTCOME,
        headerName: ACTION_FIELD_LABELS[ACTION_CELL_KEYS.DESIRED_OUTCOME],
        flex: 1,
        editable: true,
        sortable: false,
        width: 255,
        xxlWidth: 320,
        align: 'left',
        headerAlign: 'left',
        preProcessEditCellProps: validateRequiredField(ACTION_CELL_KEYS.DESIRED_OUTCOME, checkTextEmpty),
        renderCell: (params) => <DataGridMultilineEditCell value={params.value} />,
        renderEditCell: (params) => <DataGridMultilineEditCell params={params} />,
      },
      {
        field: ACTION_CELL_KEYS.STATUS,
        headerName: ACTION_FIELD_LABELS[ACTION_CELL_KEYS.STATUS],
        width: 190,
        xxlWidth: 200,
        editable: true,
        type: DATA_GRID_COLUMN_TYPE.SINGLE_SELECT,
        valueOptions: (params) =>
          [...(!params.row ? [ALL_OPTIONS_VALUE] : []), ...ORDERED_ACTIONS_STATUSES].map((status) => ({
            value: status,
            label: ACTION_STATUS_LABELS[status] || 'All',
          })),
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => (
          <DataGridStatusChip label={ACTION_STATUS_LABELS[params.value]} color={ACTION_STATUS_COLORS[params.value]} />
        ),
        renderEditCell: (params) => (
          <DataGridStatusSelect
            params={params}
            options={ORDERED_ACTIONS_STATUSES}
            labels={ACTION_STATUS_LABELS}
            colors={ACTION_STATUS_COLORS}
          />
        ),
      },
      {
        field: ACTION_CELL_KEYS.DEADLINE,
        headerName: ACTION_FIELD_LABELS[ACTION_CELL_KEYS.DEADLINE],
        width: 180,
        editable: true,
        type: DATA_GRID_COLUMN_TYPE.DATE,
        headerAlign: 'left',
        /** @type {import('@mui/x-data-grid-pro').GridComparatorFn} */
        sortComparator: (v1, v2, param1, param2) => {
          if (sortModel?.field !== ACTION_CELL_KEYS.DEADLINE) {
            return 0;
          }

          if (param1.id === ADD_ROW_ID || param1.id === PENDING_ROW_ID) {
            return sortModel.sort === SORT_OPTIONS.ASC ? 1 : -1;
          }

          if (param2.id === ADD_ROW_ID || param2.id === PENDING_ROW_ID) {
            return sortModel.sort === SORT_OPTIONS.ASC ? -1 : 1;
          }

          return new Date(v1).getTime() - new Date(v2).getTime();
        },
        renderCell: (params) => (
          <DataGridDateChip
            isPendingRow={params.row.id === PENDING_ROW_ID}
            dateValue={params.value}
            status={params.row.status}
          />
        ),
        renderEditCell: (params) => (
          <DataGridDatePicker isPendingRow={params.row.id === PENDING_ROW_ID} params={params} />
        ),
      },
      detailColumnConfig ? detailColumnConfig.column : ownerColumn,
      ...(withEditPermissions
        ? [
            {
              field: ACTION_TRACKER_CELL_KEYS.OPTIONS,
              headerName: 'Options',
              type: DATA_GRID_COLUMN_TYPE.ACTIONS,
              width: 90,
              align: 'center',
              getActions: ({ row }) => getActionItemActions(row),
            },
          ]
        : []),
    ];
  }, [sortModel, fetchActionOwners, withEditPermissions, getActionItemActions, detailColumnConfig]);

  const rows = useMemo(() => {
    const rowsList = (entry?.actions || []).toSorted((a, b) => new Date(a.created_at) - new Date(b.created_at));

    if (withEditPermissions && !isReadOnlyJournal) {
      if (hasPendingRowForEntry && pendingRow) {
        rowsList.push({
          id: PENDING_ROW_ID,
          [ACTION_CELL_KEYS.ACTION_TEXT]: pendingRow[ACTION_CELL_KEYS.ACTION_TEXT] ?? '',
          [ACTION_CELL_KEYS.DESIRED_OUTCOME]: pendingRow[ACTION_CELL_KEYS.DESIRED_OUTCOME] ?? '',
          [ACTION_CELL_KEYS.STATUS]: pendingRow[ACTION_CELL_KEYS.STATUS] ?? ACTION_STATUSES.NOT_STARTED,
          [ACTION_CELL_KEYS.DEADLINE]: pendingRow[ACTION_CELL_KEYS.DEADLINE] ?? null,
          [detailField]: pendingRow[detailField] ?? detailColumnConfig?.defaultValue ?? null,
        });
      } else {
        rowsList.push({ id: ADD_ROW_ID, action_text: '+ Add item' });
      }
    }

    return rowsList;
  }, [
    entry.actions,
    hasPendingRowForEntry,
    isReadOnlyJournal,
    pendingRow,
    withEditPermissions,
    detailField,
    detailColumnConfig,
  ]);

  const onTableSort = useCallback((newSortModel) => {
    setSortModel(newSortModel || null);
  }, []);

  return (
    <div onClickCapture={handleCellClickCapture}>
      <DataGridTable
        apiRef={apiRef}
        hideFooter
        withTransparentHeader
        sortingMode="client"
        filterMode="client"
        itemsName="Actions"
        sortingOrder={[SORT_OPTIONS.ASC, SORT_OPTIONS.DESC, null]}
        rows={rows}
        columns={columns}
        processRowUpdate={handleProcessRowUpdate}
        onProcessRowUpdateError={(error) => console.error(error)}
        onCellClick={handleCellClick}
        isCellEditable={(params) => {
          if (params.id === ADD_ROW_ID) {
            return false;
          }

          if (!withEditPermissions) {
            return (
              params.field === ACTION_CELL_KEYS.STATUS && params.row[ACTION_CELL_KEYS.OWNER_DETAILS]?.id === userId
            );
          }

          const error = cellValidationErrorRef.current;

          return !error || (error.rowId === params.id && error.field === params.field);
        }}
        getRowId={(row) => row.id}
        getRowClassName={(params) => ([ADD_ROW_ID, PENDING_ROW_ID].includes(params.id) ? 'filled-row' : '')}
        getCellClassName={(params) => (params.id === ADD_ROW_ID ? 'add-row-cell' : '')}
        onRowClick={(params) => {
          if (params.id === ADD_ROW_ID) {
            handleAddRow();
          }
        }}
        getRowSpacing={() => ({})}
        getRowHeight={() => 'auto'}
        pageSize={rows.length}
        rowCount={rows.length}
        customStyles={{
          dataGrid: styles.dataGrid,
          emptyTable: styles.emptyTable,
        }}
        onTableSort={onTableSort}
        sortModel={sortModel}
        filterModelItems={filterModelItems}
        onFilterModelChange={setFilterModelItems}
        excludedRowIds={[ADD_ROW_ID, PENDING_ROW_ID]}
        onCellEditStop={() => {
          cellValidationErrorRef.current = null;
        }}
        onCellEditStart={(params, event) => {
          const error = cellValidationErrorRef.current;

          if (error && (error.rowId !== params.id || error.field !== params.field)) {
            // eslint-disable-next-line no-param-reassign
            event.defaultMuiPrevented = true;

            return;
          }

          if (params.reason === 'printableKeyDown' && !printableFields.includes(params.field)) {
            // eslint-disable-next-line no-param-reassign
            event.defaultMuiPrevented = true;
          }
        }}
        columnHeaderHeight={84}
      />
    </div>
  );
};

JournalActionsTable.propTypes = {
  entry: PropTypes.shape({
    id: PropTypes.string,
    actions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        action_text: PropTypes.string,
        desired_outcome: PropTypes.string,
        status: PropTypes.string,
        deadline: PropTypes.string,
        owner: PropTypes.number,
        owner_details: PropTypes.shape({
          id: PropTypes.number,
          full_name: PropTypes.string,
          email: PropTypes.string,
        }),
      }),
    ),
  }).isRequired,
  fetchActionOwners: PropTypes.func,
  onUpdateAction: PropTypes.func.isRequired,
  onDeleteAction: PropTypes.func,
  onGoToManageUsers: PropTypes.func,
  pendingRow: PropTypes.shape({
    entryId: PropTypes.string,
    [ACTION_CELL_KEYS.ACTION_TEXT]: PropTypes.string,
    [ACTION_CELL_KEYS.DESIRED_OUTCOME]: PropTypes.string,
    [ACTION_CELL_KEYS.STATUS]: PropTypes.string,
    [ACTION_CELL_KEYS.DEADLINE]: PropTypes.instanceOf(Date),
    [ACTION_CELL_KEYS.OWNER_DETAILS]: PropTypes.shape({
      id: PropTypes.number,
      full_name: PropTypes.string,
      email: PropTypes.string,
    }),
  }),
  onCreatePendingRow: PropTypes.func.isRequired,
  onUpdatePendingRow: PropTypes.func.isRequired,
  onCancelPendingRow: PropTypes.func.isRequired,
  onSubmitPendingRow: PropTypes.func.isRequired,
  isReadOnlyJournal: PropTypes.bool.isRequired,
  withEditPermissions: PropTypes.bool,
  detailColumnConfig: PropTypes.shape({
    field: PropTypes.string.isRequired,
    isRequired: PropTypes.bool,
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    column: PropTypes.shape({}).isRequired,
  }),
  getRowMenuItems: PropTypes.func,
  defaultSortModel: PropTypes.shape({
    field: PropTypes.string,
    sort: PropTypes.oneOf(Object.values(SORT_OPTIONS)),
  }),
};
